import React, { useState, useEffect } from 'react';
import {
  Bot, LineChart, ArrowRight, ShieldCheck, Zap, Mail, Clock,
  Loader2, CheckCircle, X, Menu, Sparkles, TrendingUp, ChevronRight, Flame,
} from 'lucide-react';
import axios from 'axios';
import KairosisLogo from '@/components/KairosisLogo';
import TrubonLogo from '../components/TrubonLogo';
import TaglineReveal from '../components/TaglineReveal';
import CompanyFooter from "@/components/CompanyFooter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WhatsAppIcon = ({ size = 28 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width={size} height={size} fill="currentColor">
    <path d="M16 2C8.268 2 2 8.268 2 16c0 2.47.643 4.788 1.77 6.8L2 30l7.38-1.74A13.93 13.93 0 0 0 16 30c7.732 0 14-6.268 14-14S23.732 2 16 2zm0 25.5a11.44 11.44 0 0 1-5.84-1.6l-.42-.25-4.38 1.03 1.06-4.27-.27-.44A11.5 11.5 0 1 1 16 27.5zm6.29-8.63c-.34-.17-2.02-1-2.34-1.11-.32-.11-.55-.17-.78.17-.23.34-.9 1.11-1.1 1.34-.2.23-.4.26-.74.09-.34-.17-1.44-.53-2.74-1.69-1.01-.9-1.69-2.02-1.89-2.36-.2-.34-.02-.53.15-.7.15-.15.34-.4.51-.6.17-.2.23-.34.34-.57.11-.23.06-.43-.03-.6-.09-.17-.78-1.88-1.07-2.58-.28-.68-.57-.59-.78-.6h-.67c-.23 0-.6.09-.91.43-.31.34-1.2 1.17-1.2 2.85s1.22 3.3 1.4 3.53c.17.23 2.42 3.7 5.86 5.19.82.35 1.46.56 1.95.72.82.26 1.57.22 2.16.13.66-.1 2.02-.82 2.31-1.62.29-.8.29-1.48.2-1.62-.09-.14-.31-.23-.65-.4z"/>
  </svg>
);

function WaitlistModal({ isOpen, onClose, serviceId, serviceTitle }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/waitlist/join`, { email, service: serviceId });
      setSuccess(true);
      setMessage(res.data.message);
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to join waitlist. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setSuccess(false);
    setMessage('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={handleClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
      <div
        className="relative w-full sm:max-w-md bg-[#0D0D0D] border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 sm:p-8 shadow-[0_0_60px_rgba(0,230,118,0.1)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={handleClose} className="absolute top-4 right-4 text-[#71717A] hover:text-white transition-colors">
          <X className="w-5 h-5" />
        </button>

        {!success ? (
          <>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-[#00E676]" />
                <h3 className="text-xl font-bold text-white">Join Waitlist</h3>
              </div>
              <p className="text-[#A1A1AA] text-sm">
                Enter your email to join the waitlist for <strong className="text-white">{serviceTitle}</strong>.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00E676]/50 transition-all"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl bg-[#00E676] hover:bg-[#00C853] text-black font-semibold text-base transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <>Request to Join Waitlist</>}
              </button>
              {message && !success && <p className="text-red-400 text-sm text-center">{message}</p>}
            </form>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-[#00E676]/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#00E676]" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">You're on the list!</h3>
            <p className="text-[#A1A1AA] text-sm mb-6">{message}</p>
            <button onClick={handleClose} className="px-6 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white font-medium transition-all">
              Got it
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function AnimatedCounter({ target, prefix = '$', suffix = '' }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [target]);

  return (
    <span className="font-data text-4xl sm:text-5xl font-bold kairosis-shimmer-text">
      {prefix}{count}{suffix}
    </span>
  );
}

export default function KairosisHome() {
  const [waitlistModal, setWaitlistModal] = useState({ open: false, serviceId: '', serviceTitle: '' });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const products = [
    {
      id: 'alphabot',
      title: 'Alphabot',
      description: 'Fully automated MT5 trading bot that executes profitable trades 24/7. No emotions, just consistent returns.',
      icon: <Bot className="w-9 h-9 text-[#00E676]" />,
      actionText: 'Explore Alphabot',
      actionLink: '/alphabot',
      accent: '#00E676',
      tag: 'Live Now',
      showWaitlist: false,
      featured: true,
    },
    {
      id: 'trubon',
      title: 'Trubon',
      description: 'Save the prices you care about. When one is reached, an AI reads the moment and a deterministic risk engine decides whether to trade it.',
      // Sized to sit in the same framed box as every other product icon, so the
      // row keeps the height the card layout expects.
      icon: <TrubonLogo size="md" showText={false} />,
      actionText: 'Explore Trubon',
      actionLink: '/trubon',
      accent: '#007AFF',
      // Beta, not "Live Now". The app is usable and improving, and the people
      // most entitled to know that are the ones about to connect a broker.
      tag: 'Beta',
      showWaitlist: false,
      featured: false,
    },
    {
      id: 'roar',
      title: 'Roar',
      description: 'Track your allocated capital in real time — deposits, profits, withdrawals, and your 70% earnings share.',
      icon: <Flame className="w-9 h-9 text-[#FF6D00]" />,
      actionText: 'Open Roar',
      actionLink: '/roar',
      accent: '#FF6D00',
      tag: 'Live Now',
      showWaitlist: false,
      featured: false,
    },
  ];

  const openWaitlist = (product) => {
    setWaitlistModal({ open: true, serviceId: product.id, serviceTitle: product.title });
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white font-sans overflow-x-hidden selection:bg-[#00E676]/30">

      {/* Background. Flat, per the visual system: no gradients and nothing that
          animates on a loop. The grid and the noise are static textures, which
          give the surface depth without moving. */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 kairosis-grid-bg" />
        <div className="absolute inset-0 kairosis-noise" />
      </div>

      {/* WhatsApp */}
      <a
        href="https://wa.link/og6izz"
        target="_blank"
        rel="noopener noreferrer"
        title="Chat with us on WhatsApp"
        className="fixed bottom-5 right-4 sm:bottom-6 sm:right-6 z-[9999] flex items-center justify-center w-13 h-13 sm:w-14 sm:h-14 rounded-full bg-[#25D366] cursor-pointer no-underline shadow-[0_4px_24px_rgba(37,211,102,0.5)] hover:scale-110 transition-transform duration-300"
        style={{ animation: 'wa-pulse 2.5s infinite' }}
      >
        <WhatsAppIcon />
        <style>{`
          @keyframes wa-pulse {
            0%   { box-shadow: 0 0 0 0 rgba(37,211,102,0.5); }
            70%  { box-shadow: 0 0 0 14px rgba(37,211,102,0); }
            100% { box-shadow: 0 0 0 0 rgba(37,211,102,0); }
          }
        `}</style>
      </a>

      {/* Navigation */}
      <nav className="relative z-50 flex items-center justify-between px-4 sm:px-6 lg:px-8 py-4 border-b border-white/[0.06] backdrop-blur-xl bg-black/40">
        <a href="/" className="hover:opacity-90 transition-opacity">
          <KairosisLogo size="md" />
        </a>

        <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-400">
          <a href="#ecosystem" className="hover:text-white transition-colors">Products</a>
          <a href="#trust" className="hover:text-white transition-colors">About</a>
          <a href="#contact" className="hover:text-white transition-colors">Contact</a>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <a
            href="/roar"
            className="hidden sm:flex px-5 py-2 rounded-full bg-[#FF6D00]/10 hover:bg-[#FF6D00]/20 border border-[#FF6D00]/30 transition-all text-sm font-medium items-center gap-2 text-[#FF9100]"
          >
            Roar <ArrowRight className="w-4 h-4" />
          </a>
          <a
            href="/alphabot"
            className="hidden sm:flex px-5 py-2 rounded-full bg-[#00E676]/15 hover:bg-[#00E676]/25 border border-white/10 transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] text-sm font-semibold items-center gap-2"
          >
            Alphabot <ArrowRight className="w-4 h-4" />
          </a>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="relative z-40 md:hidden border-b border-white/[0.06] bg-black/90 backdrop-blur-xl px-4 py-4 space-y-1">
          {[
            { href: '#ecosystem', label: 'Products' },
            { href: '#trust', label: 'About' },
            { href: '#contact', label: 'Contact' },
            { href: '/roar', label: 'Open Roar', external: true },
            { href: '/alphabot', label: 'Go to Alphabot', external: true },
          ].map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-between py-3 px-4 rounded-xl hover:bg-white/5 text-gray-300 hover:text-white transition-colors"
            >
              {link.label}
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </a>
          ))}
        </div>
      )}

      {/* Hero */}
      <main className="relative z-10 flex flex-col items-center justify-center px-4 sm:px-6 pt-14 sm:pt-24 md:pt-32 pb-16 sm:pb-24 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] border border-white/10 text-xs sm:text-sm font-medium text-gray-300 mb-8 backdrop-blur-sm">
          <Sparkles className="w-3.5 h-3.5 text-[#00E676]" />
          Trading desks running since 2024
        </div>

        <h1
          className="text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight mb-6 max-w-[680px] text-transparent bg-clip-text"
          style={{ backgroundImage: 'linear-gradient(to right, #FFFFFF, #9B9B9B)' }}
        >
          Automated trading systems,
          <br />
          built and run by us.
        </h1>

        <p className="max-w-[680px] text-base sm:text-lg text-gray-400 mb-10 leading-relaxed">
          We build the bots, manage the capital and carry the risk controls. You
          see every position, every fill and every number in real time.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto max-w-sm sm:max-w-none mx-auto">
          <a
            href="#ecosystem"
            className="group px-8 py-4 rounded-2xl bg-white text-black font-semibold text-base sm:text-lg hover:bg-gray-100 transition-all hover:scale-[1.02] active:scale-95 shadow-[0_0_40px_rgba(255,255,255,0.15)] flex items-center justify-center gap-2"
          >
            Explore Products
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="/alphabot"
            className="px-8 py-4 rounded-2xl bg-[#1F1F1F] text-white font-semibold text-base border border-white/10 hover:border-[#00E676]/40 transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
          >
            <Bot className="w-5 h-5 text-[#00E676]" />
            Launch Alphabot
          </a>
        </div>

        {/* Hero stat pill */}
        <div className="mt-16 sm:mt-20 flex flex-wrap justify-center gap-4 sm:gap-8">
          <div className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-white/[0.03] border border-white/[0.08] backdrop-blur-sm">
            <TrendingUp className="w-5 h-5 text-[#00E676]" />
            <div className="text-left">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Active capital</p>
              <p className="font-data text-lg font-semibold text-[#00E676]">$118,400</p>
            </div>
          </div>
          <div className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-white/[0.03] border border-white/[0.08] backdrop-blur-sm">
            <Zap className="w-5 h-5 text-[#007AFF]" />
            <div className="text-left">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Uptime, 90 days</p>
              <p className="font-data text-lg font-semibold text-white">99.4%</p>
            </div>
          </div>
        </div>
      </main>

      {/* Products */}
      <section id="ecosystem" className="relative z-10 px-4 sm:px-6 lg:px-8 py-16 sm:py-24 max-w-6xl mx-auto">
        <div className="text-center mb-12 sm:mb-16">
          <p className="text-[#00E676] text-sm font-medium uppercase tracking-widest mb-3">Our Products</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">Built for Traders</h2>
          <p className="text-gray-400 max-w-lg mx-auto text-sm sm:text-base">
            Intelligent tools designed to give you an edge in the markets.
          </p>
        </div>

        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-4 px-6 py-4 rounded-2xl bg-[#0A0A0A]/80 border border-[#00E676]/20 backdrop-blur-sm">
            <p className="text-gray-400 text-sm uppercase tracking-widest">Active Capital Managed</p>
            <p className="font-data text-2xl sm:text-3xl font-bold text-[#00E676] kairosis-shimmer-text">$100K</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
          {products.map((product) => (
            <div
              key={product.id}
              className={`group relative flex flex-col p-6 sm:p-8 rounded-3xl bg-[#181818] border transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] hover:-translate-y-1 ${
                product.featured
                  ? 'border-[#00E676]/30 shadow-[0_0_40px_rgba(0,230,118,0.08)] md:col-span-2 md:flex-row md:items-center md:gap-8'
                  : 'border-white/[0.08] hover:border-white/20'
              }`}
            >
              <div
                className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ background: `radial-gradient(ellipse at top left, ${product.accent}12, transparent 60%)` }}
              />

              <div className={`relative z-10 ${product.featured ? 'md:flex-1' : ''}`}>
                <div className="flex justify-between items-start mb-5">
                  <div
                    className="p-3.5 rounded-2xl border border-white/[0.08] backdrop-blur-sm"
                    style={{ background: `${product.accent}10` }}
                  >
                    {product.icon}
                  </div>
                  <span
                    className="px-3 py-1 text-xs font-semibold rounded-full border"
                    style={{ color: product.accent, borderColor: `${product.accent}40`, background: `${product.accent}10` }}
                  >
                    {product.tag}
                  </span>
                </div>

                <h3 className="text-2xl sm:text-3xl font-bold mb-3">{product.title}</h3>
                <p className="text-gray-400 text-sm sm:text-base leading-relaxed mb-6">{product.description}</p>
              </div>

              <div className={`relative z-10 space-y-2 ${product.featured ? 'md:w-64 md:shrink-0' : ''}`}>
                {product.actionLink && product.actionLink !== '#' && (
                  <a
                    href={product.actionLink}
                    className="w-full py-3.5 rounded-xl font-semibold flex justify-center items-center gap-2 transition-all hover:scale-[1.02] active:scale-95"
                    style={{
                      background: product.id === 'alphabot'
                        ? `linear-gradient(135deg, ${product.accent}, #00BFA5)`
                        : `linear-gradient(135deg, ${product.accent}, ${product.id === 'roar' ? '#FF9100' : product.accent})`,
                      color: '#000',
                      boxShadow: `0 0 30px ${product.accent}30`,
                    }}
                  >
                    {product.actionText} <ArrowRight className="w-4 h-4" />
                  </a>
                )}
                {product.showWaitlist && (
                  <button
                    onClick={() => openWaitlist(product)}
                    className="w-full py-3.5 rounded-xl border font-medium flex justify-center items-center gap-2 transition-all hover:bg-white/5"
                    style={{ borderColor: `${product.accent}40`, color: product.accent }}
                  >
                    <Clock className="w-4 h-4" />
                    Join Waitlist
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Trust */}
      <TaglineReveal />

      <section id="trust" className="relative z-10 py-16 sm:py-24 border-t border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <div className="flex-1 space-y-6 text-center lg:text-left">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#007AFF]/10 text-[#007AFF] border border-[#007AFF]/20">
                <ShieldCheck className="w-7 h-7" />
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
                Built on Trust &{' '}
                <span className="kairosis-shimmer-text">Transparency</span>
              </h2>
              <p className="text-gray-400 text-base sm:text-lg max-w-lg mx-auto lg:mx-0">
                Every system we deploy is backed by rigorous risk management, secure infrastructure, and real performance data.
              </p>
              <ul className="space-y-3 text-left max-w-md mx-auto lg:mx-0">
                {['Strict risk management protocols', 'Secure payment gateways', 'Transparent performance reporting'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300 text-sm sm:text-base">
                    <div className="w-5 h-5 rounded-full bg-[#00E676]/10 border border-[#00E676]/30 flex items-center justify-center shrink-0">
                      <CheckCircle className="w-3 h-3 text-[#00E676]" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex-1 w-full max-w-md lg:max-w-none">
              <div className="relative">
                
                <div className="relative bg-[#0A0A0A]/90 border border-white/10 rounded-3xl p-8 sm:p-10 backdrop-blur-xl shadow-2xl">
                  <div className="flex items-center gap-4 mb-8 pb-6 border-b border-white/[0.06]">
                    <KairosisLogo size="sm" showText={false} />
                    <div>
                      <h4 className="font-bold text-lg">Kairosis Capital</h4>
                      <p className="text-sm text-gray-500">Financial Technology</p>
                    </div>
                  </div>
                  <div className="text-center py-4">
                    <p className="text-gray-500 text-sm uppercase tracking-widest mb-3">Active Capital Managed</p>
                    <AnimatedCounter target={100} prefix="$" suffix="K" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="relative z-10 py-16 sm:py-24 border-t border-white/[0.06]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-[#007AFF] text-sm font-medium uppercase tracking-widest mb-3">Get In Touch</p>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">We're Here to Help</h2>
          <p className="text-gray-400 mb-10 sm:mb-12 text-sm sm:text-base">
            Questions or support? Reach us instantly on WhatsApp or drop us an email.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            <a
              href="https://wa.link/og6izz"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-4 p-7 sm:p-8 rounded-2xl bg-[#0A0A0A]/80 border border-[#25D366]/20 hover:border-[#25D366]/50 hover:bg-[#25D366]/5 transition-all duration-300 backdrop-blur-sm"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#25D366]/10 flex items-center justify-center group-hover:scale-110 transition-transform text-[#25D366]">
                <WhatsAppIcon size={28} />
              </div>
              <div>
                <p className="font-semibold text-white mb-1">WhatsApp</p>
                <p className="text-sm text-gray-400 group-hover:text-[#25D366] transition-colors">Chat with us instantly</p>
              </div>
              <span className="flex items-center gap-1.5 text-xs text-[#25D366]/70 group-hover:text-[#25D366] font-medium">
                Open WhatsApp <ArrowRight className="w-3 h-3" />
              </span>
            </a>

            <a
              href="mailto:support@kairosiscapital.com"
              className="group flex flex-col items-center gap-4 p-7 sm:p-8 rounded-2xl bg-[#0A0A0A]/80 border border-[#007AFF]/20 hover:border-[#007AFF]/50 hover:bg-[#007AFF]/5 transition-all duration-300 backdrop-blur-sm"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#007AFF]/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Mail className="w-7 h-7 text-[#007AFF]" />
              </div>
              <div>
                <p className="font-semibold text-white mb-1">Email Support</p>
                <p className="text-sm text-gray-400 group-hover:text-[#007AFF] transition-colors break-all">support@kairosiscapital.com</p>
              </div>
              <span className="flex items-center gap-1.5 text-xs text-[#007AFF]/70 group-hover:text-[#007AFF] font-medium">
                Send an email <ArrowRight className="w-3 h-3" />
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/[0.06] bg-black/40 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 sm:gap-8 mb-12">
            <div className="sm:col-span-2 lg:col-span-1">
              <KairosisLogo size="sm" />
              <p className="text-gray-500 text-sm mt-4 leading-relaxed max-w-xs">
                AI-powered trading technology for the modern trader. Precision. Performance. Profit.
              </p>
            </div>

            <div>
              <h5 className="text-white font-semibold text-sm mb-4 uppercase tracking-wider">Products</h5>
              <ul className="space-y-3">
                <li><a href="/alphabot" className="text-gray-500 hover:text-[#00E676] transition-colors text-sm">Alphabot</a></li>
                <li><a href="/trubon" className="text-gray-500 hover:text-white transition-colors text-sm">Trubon</a></li>
              </ul>
            </div>

            <div>
              <h5 className="text-white font-semibold text-sm mb-4 uppercase tracking-wider">Company</h5>
              <ul className="space-y-3">
                <li><a href="#trust" className="text-gray-500 hover:text-white transition-colors text-sm">About</a></li>
                <li><a href="#contact" className="text-gray-500 hover:text-white transition-colors text-sm">Contact</a></li>
              </ul>
            </div>

            <div>
              <h5 className="text-white font-semibold text-sm mb-4 uppercase tracking-wider">Connect</h5>
              <ul className="space-y-3">
                <li>
                  <a href="https://wa.link/og6izz" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#25D366] transition-colors text-sm flex items-center gap-2">
                    <WhatsAppIcon size={14} /> WhatsApp
                  </a>
                </li>
                <li>
                  <a href="mailto:support@kairosiscapital.com" className="text-gray-500 hover:text-white transition-colors text-sm flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5" /> Email
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-white/[0.06] flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-600 text-xs sm:text-sm text-center sm:text-left">
              &copy; {new Date().getFullYear()} Kairosis Capital. All rights reserved.
            </p>
            <p className="text-gray-700 text-xs text-center sm:text-right">
              Trading involves risk. Past performance is not indicative of future results.
            </p>
          </div>
        </div>
        <CompanyFooter />
      </footer>

      <WaitlistModal
        isOpen={waitlistModal.open}
        onClose={() => setWaitlistModal({ open: false, serviceId: '', serviceTitle: '' })}
        serviceId={waitlistModal.serviceId}
        serviceTitle={waitlistModal.serviceTitle}
      />
    </div>
  );
}
